// Audio Voice Note Recorder component with live timer & waveform (WP-04).
import { useState, useEffect, useRef } from 'react';
import { useI18n } from '../../lib/i18n';
import { useTenant } from '../../lib/tenant';
import { uploadInboxMediaFile, uploadMediaToMeta } from '../../lib/media';
import { TrashIcon, SendIcon } from '../icons';

interface AudioRecorderProps {
  onCancel: () => void;
  onSend: (mediaId: string) => void;
}

export default function AudioRecorder({ onCancel, onSend }: AudioRecorderProps) {
  const { t } = useI18n();
  const { tenantId } = useTenant();
  const [seconds, setSeconds] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<any>(null);

  useEffect(() => {
    let active = true;

    async function startRecording() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;

        // Pick supported mimeType
        let mimeType = 'audio/webm;codecs=opus';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/webm';
        }
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = ''; // browser default
        }

        const options = mimeType ? { mimeType } : undefined;
        const mediaRecorder = new MediaRecorder(stream, options);
        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];

        mediaRecorder.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mediaRecorder.start(250);

        timerRef.current = setInterval(() => {
          setSeconds((s) => s + 1);
        }, 1000);
      } catch (err) {
        setError(
          (err as Error).message ||
            t('inbox.audio.micPermissionError') ||
            'Microphone access denied',
        );
      }
    }

    void startRecording();

    return () => {
      active = false;
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, [t]);

  const handleCancel = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
    onCancel();
  };

  const handleFinishAndSend = async () => {
    if (!tenantId || !mediaRecorderRef.current) return;

    setUploading(true);
    setError(null);
    if (timerRef.current) clearInterval(timerRef.current);

    const recorder = mediaRecorderRef.current;

    recorder.onstop = async () => {
      try {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
        }

        const mimeType = recorder.mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });

        if (audioBlob.size === 0) {
          setError('Recorded audio was empty');
          setUploading(false);
          return;
        }

        // Upload to Firebase Storage
        const { storagePath } = await uploadInboxMediaFile(tenantId, audioBlob, 'voice.webm');

        // Transcode via ffmpeg and upload to Meta Graph API
        const metaRes = await uploadMediaToMeta({
          storagePath,
          mimeType,
          asVoiceNote: true,
        });

        if (metaRes.ok && metaRes.mediaId) {
          onSend(metaRes.mediaId);
        } else {
          throw new Error('Failed to get mediaId from Meta');
        }
      } catch (err) {
        setError((err as Error).message || 'Failed to send voice note');
        setUploading(false);
      }
    };

    recorder.stop();
  };

  const fmtDuration = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
  };

  return (
    <div className="flex w-full items-center justify-between gap-3 rounded-full bg-rose-50/80 px-4 py-2 border border-rose-200 dark:bg-rose-950/30 dark:border-rose-900/50">
      {/* Recording Indicator + Timer */}
      <div className="flex items-center gap-2.5">
        <span className="relative flex h-3 w-3">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
          <span className="relative inline-flex h-3 w-3 rounded-full bg-rose-600" />
        </span>
        <span className="font-mono text-xs font-bold text-rose-600 dark:text-rose-400">
          {fmtDuration(seconds)}
        </span>
        <span className="text-xs text-ink-3 dark:text-slate-400">
          {uploading
            ? t('inbox.audio.uploading') || 'Transcoding & sending…'
            : t('inbox.audio.recording') || 'Recording voice note…'}
        </span>
      </div>

      {error && (
        <span className="truncate text-xs font-semibold text-rose-600 dark:text-rose-400">
          {error}
        </span>
      )}

      {/* Action Buttons */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleCancel}
          disabled={uploading}
          aria-label={t('common.cancel') || 'Cancel'}
          className="grid h-8 w-8 place-items-center rounded-full text-slate-500 hover:bg-rose-100 hover:text-rose-700 disabled:opacity-50 dark:hover:bg-rose-900/50"
        >
          <TrashIcon size={16} />
        </button>
        <button
          type="button"
          onClick={handleFinishAndSend}
          disabled={uploading || seconds < 1}
          className="flex items-center gap-1.5 rounded-full bg-brand px-4 py-1.5 text-xs font-bold text-white shadow-sm transition hover:bg-brand-dark disabled:opacity-50"
        >
          <SendIcon size={14} />
          <span>{uploading ? t('common.sending') || 'Sending…' : t('common.send') || 'Send'}</span>
        </button>
      </div>
    </div>
  );
}
